import { FieldError } from './FieldError';
import styles from './ContactInfoFields.module.css';

/**
 * The one contact block for every inquiry submission path (#240 candidate
 * A10): the wizard's Contact step, the quick-enquiry form, and the dry-hire
 * form all render name/email(/phone) through this component instead of
 * hand-rolling the fields.
 */
interface ContactInfoFieldsProps {
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  details?: string;
  labels?: {
    name?: string;
    email?: string;
    phone?: string;
    details?: string;
  };
  showValidation: boolean;
  /** Per-field messages from the parent's error map. Shown (with red border +
   *  aria-invalid) only when `showValidation` is true; the map recomputes per
   *  render, so each message clears live as the field becomes valid. */
  nameError?: string;
  emailError?: string;
  phoneError?: string;
  onNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPhoneChange?: (value: string) => void;
  onDetailsChange?: (value: string) => void;
  showDetailsField?: boolean;
  /** Render the phone field at all (quick enquiries collect no phone). */
  showPhoneField?: boolean;
  /** Mark the phone label "(optional)" — dry hire collects phone but never
   *  requires it. */
  phoneOptional?: boolean;
}

export function ContactInfoFields({
  contactName,
  contactEmail,
  contactPhone = '',
  details,
  labels,
  showValidation,
  nameError,
  emailError,
  phoneError,
  onNameChange,
  onEmailChange,
  onPhoneChange,
  onDetailsChange,
  showDetailsField = false,
  showPhoneField = true,
  phoneOptional = false,
}: ContactInfoFieldsProps) {
  const showNameError = showValidation ? nameError : undefined;
  const showEmailError = showValidation ? emailError : undefined;
  const showPhoneError = showValidation ? phoneError : undefined;
  const resolvedLabels = {
    name: labels?.name ?? 'Your Name',
    email: labels?.email ?? 'Email',
    phone: labels?.phone ?? 'Phone',
    details: labels?.details ?? 'Event Details',
  };

  // Field ids follow the wizard's `inquiry-field-<name>` convention so
  // scroll-to-first-error (revealValidationErrors) can focus them, and the
  // labels are programmatically associated for assistive tech.
  const emailField = (
    <div>
      <label htmlFor="inquiry-field-contactEmail" className={styles.label}>
        {resolvedLabels.email}
      </label>
      <input
        id="inquiry-field-contactEmail"
        type="email"
        value={contactEmail}
        autoComplete="email"
        onChange={(e) => onEmailChange(e.target.value)}
        aria-invalid={showEmailError ? true : undefined}
        className={`${styles.input} ${showEmailError ? styles.invalid : ''}`}
      />
      <FieldError message={showEmailError} />
    </div>
  );

  return (
    <div className={styles.fields}>
      <div>
        <label htmlFor="inquiry-field-contactName" className={styles.label}>
          {resolvedLabels.name}
        </label>
        <input
          id="inquiry-field-contactName"
          type="text"
          value={contactName}
          autoComplete="name"
          onChange={(e) => onNameChange(e.target.value)}
          aria-invalid={showNameError ? true : undefined}
          className={`${styles.input} ${showNameError ? styles.invalid : ''}`}
        />
        <FieldError message={showNameError} />
      </div>

      {showPhoneField && onPhoneChange ? (
        <div className={styles.pair}>
          {emailField}
          <div>
            <label htmlFor="inquiry-field-contactPhone" className={styles.label}>
              {resolvedLabels.phone}
              {phoneOptional && (
                <>
                  {' '}
                  <span className={styles.optional}>(optional)</span>
                </>
              )}
            </label>
            <input
              id="inquiry-field-contactPhone"
              type="tel"
              value={contactPhone}
              autoComplete="tel"
              onChange={(e) => onPhoneChange(e.target.value)}
              aria-invalid={showPhoneError ? true : undefined}
              className={`${styles.input} ${showPhoneError ? styles.invalid : ''}`}
            />
            <FieldError message={showPhoneError} />
          </div>
        </div>
      ) : (
        emailField
      )}

      {showDetailsField && onDetailsChange && (
        <div>
          <label className={styles.label}>{resolvedLabels.details}</label>
          <textarea
            value={details || ''}
            onChange={(e) => onDetailsChange(e.target.value)}
            rows={3}
            className={styles.details}
          />
        </div>
      )}
    </div>
  );
}
